import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Initialize auth state from localStorage 
  useEffect(() => {
    const initializeAuth = async () => {
      try {        
        // Check for existing traditional auth session
        const storedToken = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('authUser');
        
        if (storedToken && storedUser) {
          try {
            // Verify token with backend
            const response = await authAPI.verifyToken(storedToken);
            if (response.success && response.data && response.data.user) {
              setToken(storedToken);
              setUser(response.data.user);
              console.log('✅ Auth session restored:', response.data.user.email);
            } else {
              // Token invalid, clear storage
              clearAuth();
            }
          } catch (error) {
            console.error('❌ Token verification failed:', error);
            clearAuth();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAuth();
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await authAPI.login({ email, password, rememberMe });
      
      if (response.success) {
        setToken(response.token);
        setUser(response.user);
        
        // Store in localStorage
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('authUser', JSON.stringify(response.user));
        
        return { success: true, user: response.user };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Errore durante il login' };
    }
  };

  const registerPrivate = async (userData) => {
    try {
      const response = await authAPI.registerPrivate(userData);
      
      if (response.success) {
        setToken(response.token);
        setUser(response.user);
        
        // Store in localStorage
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('authUser', JSON.stringify(response.user));
        
        return { success: true, user: response.user };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: error.response?.data?.detail || 'Errore durante la registrazione' };
    }
  };

  const registerBusiness = async (userData) => {
    try {
      const response = await authAPI.registerBusiness(userData);
      
      if (response.success) {
        setToken(response.token);
        setUser(response.user);
        
        // Store in localStorage
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('authUser', JSON.stringify(response.user));
        
        return { success: true, user: response.user };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Business registration error:', error);
      return { success: false, message: error.response?.data?.detail || 'Errore durante la registrazione' };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await authAPI.logout(token);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
    }
  };

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('authUser', JSON.stringify(updatedUser));
  };

  const isAuthenticated = () => {
    return !!(user && token);
  };

  const isPrivateUser = () => {
    return user?.userType === 'private';
  };

  const isBusinessUser = () => {
    return user?.userType === 'business';
  };

  const isEmailVerified = () => {
    return user?.emailVerified === true;
  };

  const value = {
    user,
    token,
    loading,
    initialized,
    login,
    registerPrivate,
    registerBusiness,
    logout,
    updateUser,
    isAuthenticated,
    isPrivateUser,
    isBusinessUser,
    isEmailVerified
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};